import { useContext, useEffect, useState } from 'react'

import { useSelector } from 'react-redux'

import FilesManager from '../../shared/FilesManager'
import { getMaccsLinkedInstitutions } from '../../../utils/api/school'
import { selectUser } from '../../../reducers/UserReducer'
import { createInstitution, oldRemoveInstitution, oldUpdateInstitution } from '../../../utils/api/institution'
import { GlobalContext } from '../../../Providers/GlobalProvider'
import { isObject, recursiveIncludes } from '../../../utils'
import { ROLE_ADMIN_RESEAU, ROLE_LOCAL_SECRETARY } from '../../../utils/constants'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import SmartTable, { SmartTableDataTypes } from '../../shared/SmartTable'

import '../../../assets/shifts-manager.scss'

const MaccsInstitutionsView = () => {
  const { countries } = useContext(GlobalContext)

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [premiumSchool, setPremiumSchool] = useState(false)
  const [institutions, setInstitutions] = useState([])
  const [selectedFiles, setSelectedFiles] = useState(null)
  const [loading, setLoading] = useState(false)

  const localCountries = countries.map(c => ({ id: c.id, name: t(`country.${c.code}`) }))

  useEffect(() => {
    if (user) {
      setLoading(true)
      setPremiumSchool(!!user.school.managed)
      loadInstitutions(user).then(() => setLoading(false))
    }
  }, [user])

  const loadInstitutions = user => {
    return getMaccsLinkedInstitutions(user, user.school).then(({ data }) => setInstitutions(data))
  }

  const handleFileManagement = institution => {
    setSelectedFiles(institution)
  }

  const isEditableRow = data => {
    if (data.id === -1) {
      return true
    }

    return recursiveIncludes(user.roles, [ROLE_LOCAL_SECRETARY, ROLE_ADMIN_RESEAU]) && !data.managed
  }

  const addInstitution = institution => {
    createInstitution(user, institution).then(() => loadInstitutions(user))
  }

  const editInstitution = institution => {
    delete institution.areas

    oldUpdateInstitution({ ...institution, country: institution.country?.id }, user).then(() => loadInstitutions(user))
  }

  const deleteInstitution = institution => {
    oldRemoveInstitution(institution, user).then(() => loadInstitutions(user))
  }

  const getColumns = () => {
    return [
      { type: SmartTableDataTypes.ID, key: 'id' },
      {
        type: SmartTableDataTypes.STRING,
        name: t('Name'),
        key: 'name',
        disabled: d => d.managed
      },
      {
        type: SmartTableDataTypes.STRING,
        name: t('Acronym'),
        key: 'acronym',
        disabled: d => d.managed
      },
      {
        type: SmartTableDataTypes.STRING,
        name: t('Address'),
        key: 'address',
        disabled: d => d.managed
      },
      {
        type: SmartTableDataTypes.STRING,
        name: t('Zipcode'),
        key: 'zipcode',
        disabled: d => d.managed
      },
      {
        type: SmartTableDataTypes.STRING,
        name: t('City'),
        key: 'city',
        disabled: d => d.managed
      },
      {
        type: SmartTableDataTypes.SELECT,
        name: t('Country'),
        dataKey: d => {
          let value = null

          if (isObject(d.country)) {
            value = d.country.id
          } else if (typeof d.country === 'number') {
            value = d.country
          }

          return value
        },
        key: 'country',
        options: localCountries,
        disabled: d => d.managed
      },
      {
        type: SmartTableDataTypes.STRING,
        name: t('Description'),
        key: 'description',
        disabled: d => d.managed
      }
    ]
  }

  const getAdditionalActions = () => {
    return [
      {
        iconName: 'file',
        type: 'primary',
        title: t('Manage files'),
        onClick: handleFileManagement
      }
    ]
  }

  return (
    <div>
      {loading && (
        <div className='fill-content'>
          <div className='table-loading-ring' />
        </div>
      )}
      <SmartTable
        columns={getColumns()}
        data={institutions}
        checkIfRowIsControllable={premiumSchool ? isEditableRow : undefined}
        onDataAdd={premiumSchool ? addInstitution : undefined}
        onDataEdit={premiumSchool ? editInstitution : undefined}
        onDataDelete={premiumSchool ? deleteInstitution : undefined}
        additionalActions={getAdditionalActions()}
      />
      <FilesManager
        entity={selectedFiles}
        entityName='Institution'
        onClose={() => setSelectedFiles(null)}
        userContextFiltered={false}
        preventChanges
      />

    </div>
  )
}

export default MaccsInstitutionsView
